// Core imports
import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment-timezone';

// Component imports
import { Card, Col, Divider, Empty, Row, Space, Tag, Tooltip, Typography, Spin } from 'antd';
import { QuestionCircleOutlined, LoadingOutlined } from '@ant-design/icons';

// Misc imports
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { COLOR_STATUS_TYPES, DATETIME_BACKEND_FORMAT, DEFAULT_DATETIME_FORMAT, LANGUAGE_OPTIONS } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_CHAT_SESSIONS } from '../../constants/navigation';
import { useFetchAutomatedTestResultByIdQuery } from '../../store/automatedTests/automatedTestsApi';

// Const
const SESSION_STATUSES = {
  PASSED: 'Passed',
  FAILED: 'Failed'
};

const formatDate = (date, timezone = 'UTC') => {
  return moment(date).tz(timezone).format(DEFAULT_DATETIME_FORMAT);
};

const formatBackendDate = (date, timezone = 'UTC') => {
  if (!date) {
    return moment.tz(moment(Date.now()), timezone).endOf('day').format(DATETIME_BACKEND_FORMAT);
  }
  return moment.tz(moment(date), timezone).startOf('day').format(DATETIME_BACKEND_FORMAT);
};

const AutomatedAgentTestsResultsView = ({ resultId }) => {
  const { orgId } = useParams();
  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });

  const { data: activeResult, isFetching } = useFetchAutomatedTestResultByIdQuery(
    { orgId, id: resultId },
    { skip: !orgId || !resultId }
  );

  const renderSuccessRateTag = (successRate) => {
    let color;
    if (successRate >= 80) {
      color = COLOR_STATUS_TYPES.SUCCESS;
    } else if (successRate >= 70) {
      color = COLOR_STATUS_TYPES.WARNING;
    } else {
      color = COLOR_STATUS_TYPES.ERROR;
    }

    const safeRate = typeof successRate === 'number' ? successRate : 0;

    return (
      <Tag color={color} className="fs-xxl d-flex justify-content-center align-items-center h-100 w-100 border-radius-lg">
        <Typography.Text className={`text-${color} fw-600`}>
          {`${safeRate % 1 === 0 ? safeRate : safeRate.toFixed(2)}% of Sessions Passed`}
        </Typography.Text>
      </Tag>
    );
  };

  if (isFetching) {
    return (
      <Row justify="center" align="middle" className="h-100 min-h-250">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </Row>
    );
  }

  if (!activeResult || isEmptyArray(activeResult?.results)) {
    return <Empty description={t('No detailed results found for this run.')} className="mt-10" />;
  }

  return (
    <Row>
      <Col xs={24}>
        <Row>
          <Col xs={16}>
            <Divider orientation="left" className="m-0 pr-2 py-2">
              <Typography.Text>
                {t('Overall')}
                <Tooltip
                  className="ml-1"
                  overlayStyle={{ maxWidth: 350 }}
                  title={t('Average outcome of all sessions, shown as the percentage that passed.')}
                >
                  <QuestionCircleOutlined className="v-align-top fs-base" />
                </Tooltip>
              </Typography.Text>
            </Divider>
          </Col>
          <Col xs={8}>
            <Divider orientation="left" className="m-0 pr-2 py-2">
              <Typography.Text>
                {t('Test Details')}
                <Tooltip
                  className="ml-1"
                  overlayStyle={{ maxWidth: 350 }}
                  title={t(`Quick reference of the test's setup and metadata.`)}
                >
                  <QuestionCircleOutlined className="v-align-top fs-base" />
                </Tooltip>
              </Typography.Text>
            </Divider>
          </Col>
        </Row>
        <Row>
          <Col xs={16}>
            <div className="d-flex justify-content-center align-items-center w-100 h-100">
              {renderSuccessRateTag(activeResult?.successRate)}
            </div>
          </Col>
          <Col xs={8}>
            <Card
              bordered={false}
              bodyStyle={{ height: '100%', overflowY: 'auto', maxHeight: '400px' }}
              className="shadow-none border-none bg-layout-gray h-100"
            >
              <ul style={{ listStyleType: 'disc' }} className="m-0 pl-2">
                <li className="mb-2">
                  <Typography.Text>{activeResult?.resultDetails?.testName || 'N/A'}</Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>{activeResult?.resultDetails?.testDescription || 'N/A'}</Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>
                    <Space size="small">
                      {LANGUAGE_OPTIONS.find(lang => lang.key === activeResult?.resultDetails?.languageCode)?.label || 'N/A'}
                    </Space>
                  </Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>{activeResult?.resultDetails?.examinedTenantName || 'N/A'}</Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>{activeResult?.resultDetails?.flowName || 'N/A'}</Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>
                    {activeResult?.runTime ? formatDate(activeResult.runTime, organization?.timezone) : 'N/A'}
                  </Typography.Text>
                </li>
              </ul>
            </Card>
          </Col>
        </Row>
        <Row className="pt-5">
          <Col xs={24}>
            <Divider orientation="left" className="m-0 pr-2 py-2">
              <Typography.Text>
                {t('Detailed Results')}
                <Tooltip
                  className="ml-1"
                  overlayStyle={{ maxWidth: 350 }}
                  title={t('Per-session outcomes with reasons for pass or fail.')}
                >
                  <QuestionCircleOutlined className="v-align-top fs-base" />
                </Tooltip>
              </Typography.Text>
            </Divider>
          </Col>
          <Col xs={24}>
            <Row className="border-bottom">
              <Col xs={8} className="d-flex justify-content-center align-items-center">
                <Typography.Text className="fw-bold py-2">
                  {t('Session ID')}
                </Typography.Text>
              </Col>
              <Col xs={4} className="d-flex justify-content-center align-items-center">
                <Typography.Text className="fw-bold py-2">
                  {t('Result')}
                </Typography.Text>
              </Col>
              <Col xs={12} className="d-flex justify-content-center align-items-center">
                <Typography.Text className="fw-bold py-2">
                  {t('Explanation')}
                </Typography.Text>
              </Col>
            </Row>
            {
              activeResult?.results?.map((result, index) => {
                const color = result?.passed ? COLOR_STATUS_TYPES.SUCCESS : COLOR_STATUS_TYPES.ERROR;
                const label = result?.passed ? SESSION_STATUSES.PASSED : SESSION_STATUSES.FAILED;

                return (
                  <Row className="border-bottom" key={result?.resultId || index}>
                    <Col xs={8} className="d-flex justify-content-center align-items-center text-center px-2">
                      {
                        !activeResult?.resultDetails?.examinedTenantId ? (
                          <Typography.Text className="py-2">
                            {result?.resultId}
                          </Typography.Text>
                        ) : (
                          <Link
                            to={replaceUrlParams(NAV_CHAT_SESSIONS, { orgId }) + `?startDate=${encodeURIComponent(`${formatBackendDate(activeResult.runTime, organization?.timezone)}`)}&endDate=${encodeURIComponent(`${formatBackendDate(null, organization?.timezone)}`)}&searchBy=uuid&searchTerm=&page=0&pageSize=10&sortBy=createdAt&order=DESC&uuid=${result?.resultId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="py-2 text-break"
                          >
                            {result?.resultId}
                          </Link>
                        )
                      }
                    </Col>
                    <Col xs={4} className="d-flex justify-content-center align-items-center">
                      <Tag color={color} className="fs-lg d-flex justify-content-center align-items-center h-100 w-100 border-radius-0 mr-0">
                        <Typography.Text className={`text-${color} py-2 fw-600`} >
                          {t(label)}
                        </Typography.Text>
                      </Tag>
                    </Col>
                    <Col xs={12} className="d-flex align-items-center">
                      <Typography.Text className="p-2">
                        {result?.reason}
                      </Typography.Text>
                    </Col>
                  </Row>
                );
              })
            }
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

AutomatedAgentTestsResultsView.propTypes = {
  resultId: PropTypes.string.isRequired
};

export default AutomatedAgentTestsResultsView;
